import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HeightIcon from '@mui/icons-material/Height'
import StraightenIcon from '@mui/icons-material/Straighten'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import {
  CarrierType,
  ContainerDamageResponseDto,
  ContainerDto,
  ContainerHeight,
  ContainerPositionType,
  ContainerResponseDto,
  ContainerType,
  EquipmentType,
  JourneyCategory,
  OperationType,
  OrderResponseDto,
  OrderStatus,
  UnitType,
  YardLocation,
} from '@planning/app/api'
import { HoldsDrawerButton } from '@planning/components/hold/HoldsDrawerButton'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { ContainerJourneyStatusChipFFWrapper } from '@planning/components/molecules/ContainerJourneyStatusChipFFWrapper'
import { OrderFormStoreType } from '@planning/pages/Order/V2/stores/OrderFormStore'
import { useTranslate } from '@tolgee/react'
import { ColoredLabel, WeightIcon } from '@tom-ui/ui'
import { IFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import React, { FC, useState } from 'react'
import { OrderDamagesDialogEditButton } from './OrderDamagesDialogEditButton'
import { OrderNotesDialogEditButton } from './OrderNotesDialogEditButton'

interface Props {
  formStore?: IFormStore<OrderFormStoreType>
  initialValues?: Partial<OrderFormStoreType>
  unitAdditionalAttributes?: UnitAdditionalAttributes
  container?: ContainerCardData
  collapsable?: boolean
  defaultOpen?: boolean
  onSaveAdditionalInformation?: () => void
}

export type UnitAdditionalAttributes = {
  activeDamages?: ContainerDamageResponseDto[] | null
  orderNotes: {
    notes?: string | null
    operationalInstructions?: string | null
  }
  inboundOrder?: OrderResponseDto
  outboundOrder?: OrderResponseDto
  journey?: {
    status?: ContainerJourneyStatus
    orderStatus?: OrderStatus
    operationType?: OperationType
    inboundCarrierType?: CarrierType
    currentPosition?: ContainerPositionType
    equipmentType?: EquipmentType
    yardLocations?: YardLocation[] | null
    containerId?: number
    journeyCategory?: JourneyCategory | null
  }
}

export type ContainerCardData = {
  number?: string | null
  isoCode?: string | null
  unitType?: UnitType
  length?: number | null
  height?: ContainerHeight
  type?: ContainerType
  tare?: number | null
  maxGross?: number | null
  operator?: string | null
}

export function mapToContainerCardData(
  container?: ContainerDto | ContainerResponseDto,
): ContainerCardData | undefined {
  if (!container) return undefined

  return {
    number: container.number,
    isoCode: container.isoCode,
    unitType: container.unitType,
    length: container.length,
    height: container.height,
    type: container.type,
    tare: container.tare,
    maxGross: container.maxGross,
    operator: container.operator,
  }
}

const DirectionalHoldsDrawerButton: FC<{
  inboudOrder?: OrderResponseDto
  outboundOrder?: OrderResponseDto
}> = observer(props => {
  const { inboudOrder, outboundOrder } = props

  if (!inboudOrder && !outboundOrder) return <></>

  if (!inboudOrder && outboundOrder) return <HoldsDrawerButton order={outboundOrder} />

  if (inboudOrder && !outboundOrder) return <HoldsDrawerButton order={inboudOrder} />

  return <HoldsDrawerButton order={inboudOrder!} />
})

export const UnitContainerCard = observer(
  ({
    formStore,
    initialValues,
    unitAdditionalAttributes,
    container,
    collapsable = false,
    defaultOpen = false,
    onSaveAdditionalInformation,
  }: Props) => {
    const { t } = useTranslate()

    const { number, isoCode, unitType, length, height, type, tare, maxGross, operator } =
      container ?? {}
    const [isOpen, setIsOpen] = useState(defaultOpen)

    const payload = (maxGross ?? 0) - (tare ?? 0)

    const InfoItem = ({
      label,
      value,
      icon,
    }: {
      label: string
      value: string | number
      icon?: React.ReactNode
    }) => (
      <Stack spacing={0.5} alignItems='flex-start' mr={2}>
        <Typography variant='caption' color='text.secondary'>
          {label}
        </Typography>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {icon}
          <Typography variant='subtitle2'>{value}</Typography>
        </Stack>
      </Stack>
    )

    if (!container) return null

    return (
      <Box
        border={1}
        borderColor='divider'
        borderRadius={1}
        p={2}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Box display={'flex'} alignItems='center'>
            <Typography variant='h6'>{number ?? '-'}</Typography>
            {collapsable && (
              <IconButton onClick={() => setIsOpen(!isOpen)} sx={{ ml: 1 }}>
                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>

          {unitAdditionalAttributes && (
            <Box display='flex' alignItems='center' gap={1}>
              {unitAdditionalAttributes.journey?.journeyCategory && (
                <ColoredLabel
                  label={t(unitAdditionalAttributes.journey?.journeyCategory)}
                  color={'secondary'}
                />
              )}

              <ContainerJourneyStatusChipFFWrapper
                journeyStatus={
                  unitAdditionalAttributes?.journey?.status ?? ContainerJourneyStatus.Expected
                }
                direction={unitAdditionalAttributes?.journey?.operationType}
                inboundCarrierType={unitAdditionalAttributes?.inboundOrder?.carrierVisitType}
                currentPosition={unitAdditionalAttributes?.journey?.currentPosition}
                equipment={unitAdditionalAttributes?.journey?.equipmentType}
                yardLocations={unitAdditionalAttributes.journey?.yardLocations}
              />

              <DirectionalHoldsDrawerButton
                inboudOrder={unitAdditionalAttributes.inboundOrder}
                outboundOrder={unitAdditionalAttributes.outboundOrder}
              />
              <OrderDamagesDialogEditButton
                activeDamages={unitAdditionalAttributes.activeDamages ?? []}
                containerId={unitAdditionalAttributes.journey?.containerId ?? 0}
                onSaveAdditionalInformation={onSaveAdditionalInformation}
              />
              {formStore && (
                <OrderNotesDialogEditButton
                  {...unitAdditionalAttributes.orderNotes}
                  formStore={formStore}
                  initialValues={initialValues}
                  onSaveAdditionalInformation={onSaveAdditionalInformation}
                />
              )}
            </Box>
          )}
        </Stack>

        {(!collapsable || isOpen) && (
          <Stack direction='row' flexWrap='wrap' spacing={1} useFlexGap>
            <InfoItem label={t('iso', 'ISO')} value={isoCode ?? '-'} />
            <InfoItem label={t('unitType', 'Unit Type')} value={unitType ?? '-'} />
            <InfoItem label={t('operator', 'Operator')} value={operator ?? '-'} />
            <InfoItem
              label={t('length', 'Length')}
              value={`${length ?? '-'}ft`}
              icon={<StraightenIcon fontSize='small' />}
            />
            <InfoItem
              label={t('height', 'Height')}
              value={height ?? '-'}
              icon={<HeightIcon fontSize='small' />}
            />
            <InfoItem label={t('type', 'Type')} value={type ?? '-'} />
            <InfoItem
              label={t('tareWeight', 'Tare weight')}
              value={`${tare ?? 0}kg`}
              icon={<WeightIcon fontSize='small' />}
            />
            <InfoItem
              label={t('maxGrossWeight', 'Max gross weight')}
              value={`${maxGross ?? 0}kg`}
              icon={<WeightIcon fontSize='small' />}
            />
            <InfoItem
              label={t('maxPayload', 'Max payload')}
              value={`${payload}kg`}
              icon={<WeightIcon fontSize='small' />}
            />
          </Stack>
        )}
      </Box>
    )
  },
)
